import React, { useCallback, useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";

// Material UI
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Alert from "@mui/material/Alert";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import Collapse from "@mui/material/Collapse";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Divider from "@mui/material/Divider";
import Switch from "@mui/material/Switch";
import { alpha, useTheme } from "@mui/material/styles";

// Icons
import SettingsIcon from "@mui/icons-material/Settings";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import CancelIcon from "@mui/icons-material/Cancel";
import TuneIcon from "@mui/icons-material/Tune";
import SendIcon from "@mui/icons-material/Send";
import StorefrontIcon from "@mui/icons-material/Storefront";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

// Components
import {
  AnimatedModal,
  ModalContent,
  ModalHeader,
} from "../../modals/AnimatedModal";

// Services
import {
  getWhatsAppNotificationConfig,
  setWhatsAppNotificationConfig,
  getWhatsAppPresets,
  sendWhatsAppTest,
  getWhatsAppInstanceRestaurants,
  setWhatsAppInstanceRestaurants,
} from "../../../api/DeliveryCenter/whatsapp";
import MerchantsService from "../../../api/merchant";
import { dispatchSnackBarMessage } from "../../../utils/snackbar";

const TRIGGER_ICONS = {
  order_confirmed: CheckCircleIcon,
  order_dispatched: LocalShippingIcon,
  order_ready: RestaurantIcon,
  order_delivered: DoneAllIcon,
  order_cancelled: CancelIcon,
};

const TRIGGER_PALETTE = {
  order_confirmed: "success",
  order_dispatched: "info",
  order_ready: "warning",
  order_delivered: "success",
  order_cancelled: "error",
};

const PRESET_ICONS = {
  order_notifications: NotificationsActiveIcon,
  complete: DoneAllIcon,
  custom: TuneIcon,
};

const WA_GREEN = "#25D366";
const WA_GREEN_DARK = "#1da851";

// ─── Preset Card ────────────────────────────────────────────────────────────

function PresetCard({ presetKey, preset, isSelected, onClick }) {
  const theme = useTheme();
  const Icon = PRESET_ICONS[presetKey] || TuneIcon;

  return (
    <Paper
      elevation={isSelected ? 3 : 0}
      onClick={onClick}
      sx={{
        p: 1.5,
        cursor: "pointer",
        borderRadius: 2,
        flex: 1,
        border: `2px solid ${
          isSelected ? WA_GREEN : alpha(theme.palette.divider, 0.4)
        }`,
        backgroundColor: isSelected
          ? alpha(WA_GREEN, 0.05)
          : theme.palette.background.paper,
        transition: "all 0.18s ease",
        "&:hover": {
          borderColor: isSelected ? WA_GREEN : alpha(WA_GREEN, 0.4),
          transform: "translateY(-1px)",
        },
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: isSelected ? alpha(WA_GREEN, 0.12) : alpha(theme.palette.grey[500], 0.1),
            color: isSelected ? WA_GREEN : theme.palette.grey[500],
            borderRadius: 1.5,
            p: 0.75,
            width: 34,
            height: 34,
            flexShrink: 0,
          }}
        >
          <Icon fontSize="small" />
        </Box>
        <Box>
          <Typography variant="subtitle2" fontWeight={600} lineHeight={1.2}>
            {preset.label}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {preset.description}
          </Typography>
        </Box>
      </Stack>
    </Paper>
  );
}

// ─── Variable Chip ───────────────────────────────────────────────────────────

function VariableChip({ variable, description, onInsert }) {
  return (
    <Chip
      label={`{${variable}}`}
      size="small"
      variant="outlined"
      onClick={() => onInsert(`{${variable}}`)}
      title={description}
      sx={{
        mr: 0.5,
        mb: 0.5,
        cursor: "pointer",
        fontSize: "0.7rem",
        "&:hover": { backgroundColor: "action.hover" },
      }}
    />
  );
}

// ─── Trigger Row ─────────────────────────────────────────────────────────────

function TriggerRow({
  triggerKey,
  trigger,
  label,
  templateVariables,
  testPhone,
  sendingTest,
  onToggle,
  onTemplateChange,
  onInsertVariable,
  onSendTest,
}) {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(false);
  const Icon = TRIGGER_ICONS[triggerKey] || CheckCircleIcon;
  const palette = TRIGGER_PALETTE[triggerKey] || "primary";
  const color = theme.palette[palette]?.main || theme.palette.primary.main;

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          py: 1.25,
          px: 0.5,
        }}
      >
        {/* Icon */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: trigger.enabled ? alpha(color, 0.1) : alpha(theme.palette.grey[500], 0.08),
            color: trigger.enabled ? color : theme.palette.grey[400],
            borderRadius: 1.5,
            width: 34,
            height: 34,
            flexShrink: 0,
            transition: "all 0.2s ease",
          }}
        >
          <Icon fontSize="small" />
        </Box>

        {/* Label */}
        <Typography
          variant="body2"
          fontWeight={500}
          sx={{
            flex: 1,
            color: trigger.enabled ? "text.primary" : "text.disabled",
            transition: "color 0.2s ease",
          }}
        >
          {label || triggerKey}
        </Typography>

        {/* Expand template button (only when enabled) */}
        {trigger.enabled && (
          <Button
            size="small"
            variant="text"
            endIcon={expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
            onClick={() => setExpanded((v) => !v)}
            sx={{
              textTransform: "none",
              fontSize: "0.75rem",
              color: "text.secondary",
              minWidth: 0,
              px: 1,
            }}
          >
            Template
          </Button>
        )}

        {/* Toggle */}
        <Switch
          checked={trigger.enabled}
          onChange={onToggle}
          size="small"
          sx={{
            "& .MuiSwitch-switchBase.Mui-checked": { color: WA_GREEN },
            "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
              backgroundColor: WA_GREEN,
            },
          }}
        />
      </Box>

      {/* Template editor */}
      <Collapse in={trigger.enabled && expanded}>
        <Box
          sx={{
            ml: "46px",
            mb: 1.5,
            p: 1.5,
            borderRadius: 2,
            backgroundColor: alpha(theme.palette.grey[100], 0.6),
            border: `1px solid ${alpha(theme.palette.divider, 0.15)}`,
          }}
        >
          <TextField
            fullWidth
            multiline
            minRows={3}
            maxRows={6}
            size="small"
            value={trigger.template}
            onChange={(e) => onTemplateChange(e.target.value)}
            placeholder="Mensagem de notificacao..."
            sx={{ mb: 1 }}
          />

          <Box sx={{ mb: 1.5 }}>
            <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
              Variaveis:
            </Typography>
            {Object.entries(templateVariables).map(([variable, desc]) => (
              <VariableChip
                key={variable}
                variable={variable}
                description={desc}
                onInsert={(v) => onInsertVariable(v)}
              />
            ))}
          </Box>

          <Button
            size="small"
            variant="outlined"
            startIcon={sendingTest ? <CircularProgress size={12} /> : <SendIcon />}
            disabled={sendingTest || !testPhone}
            onClick={onSendTest}
            sx={{
              textTransform: "none",
              fontSize: "0.75rem",
              borderColor: alpha(WA_GREEN, 0.5),
              color: WA_GREEN,
              "&:hover": {
                borderColor: WA_GREEN,
                backgroundColor: alpha(WA_GREEN, 0.05),
              },
            }}
          >
            Enviar Teste
          </Button>
          {!testPhone && (
            <Typography variant="caption" color="text.disabled" sx={{ ml: 1.5 }}>
              Informe o telefone de teste abaixo
            </Typography>
          )}
        </Box>
      </Collapse>

      <Divider />
    </Box>
  );
}

// ─── Main Modal ──────────────────────────────────────────────────────────────

const TRIGGER_ORDER = [
  "order_confirmed",
  "order_dispatched",
  "order_ready",
  "order_delivered",
  "order_cancelled",
];

export default function WhatsAppNotificationConfigModal({ show, closeModal, instanceId }) {
  const theme = useTheme();
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [error, setError] = useState("");

  const [config, setConfig] = useState(null);
  const [presetsData, setPresetsData] = useState(null);
  const [triggerLabels, setTriggerLabels] = useState({});
  const [templateVariables, setTemplateVariables] = useState({});
  const [testPhone, setTestPhone] = useState("");

  const [allRestaurants, setAllRestaurants] = useState([]);
  const [linkedRestaurantIds, setLinkedRestaurantIds] = useState(new Set());
  const [handlesPosOrders, setHandlesPosOrders] = useState(false);
  const [restaurantsDirty, setRestaurantsDirty] = useState(false);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const [configRes, presetsRes, restaurantsRes, linkedRes] = await Promise.all([
        getWhatsAppNotificationConfig(instanceId),
        getWhatsAppPresets(),
        MerchantsService.getMerchants(),
        getWhatsAppInstanceRestaurants(instanceId),
      ]);
      setConfig(configRes.data);
      setPresetsData(presetsRes.data.presets);
      setTriggerLabels(presetsRes.data.trigger_labels);
      setTemplateVariables(presetsRes.data.template_variables);
      setAllRestaurants(restaurantsRes.data || []);
      const linkedData = linkedRes.data || {};
      setLinkedRestaurantIds(new Set((linkedData.restaurants || []).map((r) => r.id)));
      setHandlesPosOrders(linkedData.handles_pos_orders || false);
      setRestaurantsDirty(false);
    } catch (e) {
      setError(e.response?.data?.error || "Erro ao carregar configuracoes.");
    } finally {
      setLoading(false);
    }
  }, [instanceId]);

  useEffect(() => {
    if (show && instanceId) {
      loadData();
      setTab(0);
    }
  }, [show, instanceId, loadData]);

  const handlePresetSelect = useCallback(
    (presetKey) => {
      if (!presetsData || !presetsData[presetKey]) return;
      setConfig({ preset: presetKey, triggers: { ...presetsData[presetKey].triggers } });
    },
    [presetsData]
  );

  const handleTriggerToggle = useCallback((triggerKey) => {
    setConfig((prev) => ({
      ...prev,
      preset: "custom",
      triggers: {
        ...prev.triggers,
        [triggerKey]: { ...prev.triggers[triggerKey], enabled: !prev.triggers[triggerKey].enabled },
      },
    }));
  }, []);

  const handleTemplateChange = useCallback((triggerKey, value) => {
    setConfig((prev) => ({
      ...prev,
      preset: "custom",
      triggers: {
        ...prev.triggers,
        [triggerKey]: { ...prev.triggers[triggerKey], template: value },
      },
    }));
  }, []);

  const handleInsertVariable = useCallback((triggerKey, variable) => {
    setConfig((prev) => {
      const currentTemplate = prev.triggers[triggerKey].template || "";
      return {
        ...prev,
        preset: "custom",
        triggers: {
          ...prev.triggers,
          [triggerKey]: {
            ...prev.triggers[triggerKey],
            template: currentTemplate + " " + variable,
          },
        },
      };
    });
  }, []);

  const handleRestaurantToggle = useCallback((restaurantId) => {
    setLinkedRestaurantIds((prev) => {
      const next = new Set(prev);
      if (next.has(restaurantId)) next.delete(restaurantId);
      else next.add(restaurantId);
      return next;
    });
    setRestaurantsDirty(true);
  }, []);

  const handleSave = useCallback(async () => {
    try {
      setSaving(true);
      const promises = [setWhatsAppNotificationConfig(instanceId, config)];
      if (restaurantsDirty) {
        promises.push(setWhatsAppInstanceRestaurants(instanceId, [...linkedRestaurantIds], handlesPosOrders));
      }
      await Promise.all(promises);
      setRestaurantsDirty(false);
      dispatchSnackBarMessage(
        `whatsapp-config-saved-${Date.now()}`,
        "Configuracoes salvas com sucesso!",
        "success"
      );
      closeModal();
    } catch (e) {
      dispatchSnackBarMessage(
        `whatsapp-config-error-${Date.now()}`,
        e.response?.data?.error || "Erro ao salvar configuracoes.",
        "error"
      );
    } finally {
      setSaving(false);
    }
  }, [instanceId, config, linkedRestaurantIds, handlesPosOrders, restaurantsDirty]);

  const handleSendTest = useCallback(
    async (triggerKey) => {
      if (!testPhone) {
        dispatchSnackBarMessage(
          `whatsapp-test-error-${Date.now()}`,
          "Informe um numero de telefone para teste.",
          "warning"
        );
        return;
      }
      try {
        setSendingTest(true);
        const trigger = config.triggers[triggerKey];
        await sendWhatsAppTest(instanceId, {
          phone: testPhone,
          trigger_key: triggerKey,
          template: trigger?.template || "",
        });
        dispatchSnackBarMessage(
          `whatsapp-test-sent-${Date.now()}`,
          "Mensagem de teste enviada!",
          "success"
        );
      } catch (e) {
        dispatchSnackBarMessage(
          `whatsapp-test-error-${Date.now()}`,
          e.response?.data?.error || "Erro ao enviar teste.",
          "error"
        );
      } finally {
        setSendingTest(false);
      }
    },
    [instanceId, testPhone, config]
  );

  const linkedCount = linkedRestaurantIds.size + (handlesPosOrders ? 1 : 0);

  return (
    <AnimatedModal
      isOpen={show}
      onClose={closeModal}
      width="80%"
      maxWidth="720px"
      forceOnTop
    >
      <ModalHeader
        title="Configuracoes da Instancia"
        icon={SettingsIcon}
        onClose={closeModal}
      />

      <ModalContent>
        <AnimatePresence>
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
            >
              <Alert
                severity="error"
                onClose={() => setError("")}
                sx={{ mx: 3, mt: 2, borderRadius: 2 }}
              >
                {error}
              </Alert>
            </motion.div>
          )}
        </AnimatePresence>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 10 }}>
            <CircularProgress />
          </Box>
        ) : config ? (
          <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
            {/* Tab bar */}
            <Box sx={{ px: 3, pt: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
              <Tabs
                value={tab}
                onChange={(_, v) => setTab(v)}
                sx={{
                  minHeight: 40,
                  "& .MuiTabs-indicator": { backgroundColor: WA_GREEN, height: 3, borderRadius: "3px 3px 0 0" },
                  "& .MuiTab-root": { minHeight: 40, textTransform: "none", fontWeight: 500, fontSize: "0.875rem" },
                  "& .Mui-selected": { color: `${WA_GREEN} !important` },
                }}
              >
                <Tab
                  icon={<StorefrontIcon fontSize="small" />}
                  iconPosition="start"
                  label={
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      <span>Restaurantes</span>
                      {linkedCount > 0 && (
                        <Chip
                          label={linkedCount}
                          size="small"
                          sx={{
                            height: 18,
                            fontSize: "0.7rem",
                            backgroundColor: alpha(WA_GREEN, 0.12),
                            color: WA_GREEN,
                          }}
                        />
                      )}
                    </Stack>
                  }
                />
                <Tab
                  icon={<NotificationsActiveIcon fontSize="small" />}
                  iconPosition="start"
                  label="Notificacoes"
                />
              </Tabs>
            </Box>

            {/* Tab content */}
            <Box sx={{ flex: 1, overflowY: "auto", px: 3, py: 2.5 }}>
              {/* ── Tab 0: Restaurantes ── */}
              {tab === 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2.5 }}>
                    <Typography variant="body2" color="text.secondary">
                      Selecione quais restaurantes usam esta instancia.
                    </Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => {
                        setLinkedRestaurantIds(new Set(allRestaurants.map((r) => r.id)));
                        setRestaurantsDirty(true);
                      }}
                      sx={{
                        textTransform: "none",
                        fontSize: "0.75rem",
                        borderRadius: 1.5,
                        borderColor: alpha(theme.palette.divider, 0.6),
                        color: "text.secondary",
                        "&:hover": { borderColor: WA_GREEN, color: WA_GREEN },
                        flexShrink: 0,
                        ml: 2,
                      }}
                    >
                      Todos
                    </Button>
                  </Stack>

                  <Stack spacing={0}>
                    <Box>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={handlesPosOrders}
                            onChange={() => {
                              setHandlesPosOrders((prev) => !prev);
                              setRestaurantsDirty(true);
                            }}
                            size="small"
                            sx={{
                              color: alpha(theme.palette.primary.main, 0.4),
                              "&.Mui-checked": { color: WA_GREEN },
                            }}
                          />
                        }
                        label={
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Typography variant="body2" fontWeight={handlesPosOrders ? 500 : 400}>
                              Balcao / PDV
                            </Typography>
                            <Chip
                              label="Pedidos internos"
                              size="small"
                              sx={{
                                fontSize: "0.65rem",
                                height: 20,
                                backgroundColor: alpha(WA_GREEN, 0.1),
                                color: WA_GREEN,
                              }}
                            />
                          </Stack>
                        }
                        sx={{
                          mx: 0,
                          py: 0.75,
                          px: 1,
                          width: "100%",
                          borderRadius: 1.5,
                          "&:hover": {
                            backgroundColor: alpha(theme.palette.action.hover, 0.5),
                          },
                        }}
                      />
                      <Divider />
                    </Box>
                  </Stack>

                  {allRestaurants.length > 0 ? (
                    <Stack spacing={0}>
                      {allRestaurants.map((restaurant) => {
                        const checked = linkedRestaurantIds.has(restaurant.id);
                        return (
                          <Box key={restaurant.id}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={checked}
                                  onChange={() => handleRestaurantToggle(restaurant.id)}
                                  size="small"
                                  sx={{
                                    color: alpha(theme.palette.primary.main, 0.4),
                                    "&.Mui-checked": { color: WA_GREEN },
                                  }}
                                />
                              }
                              label={
                                <Typography variant="body2" fontWeight={checked ? 500 : 400}>
                                  {restaurant.name}
                                </Typography>
                              }
                              sx={{
                                mx: 0,
                                py: 0.75,
                                px: 1,
                                width: "100%",
                                borderRadius: 1.5,
                                "&:hover": {
                                  backgroundColor: alpha(theme.palette.action.hover, 0.5),
                                },
                              }}
                            />
                            <Divider />
                          </Box>
                        );
                      })}
                    </Stack>
                  ) : (
                    <Box
                      sx={{
                        py: 6,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 1,
                        color: "text.disabled",
                      }}
                    >
                      <StorefrontIcon sx={{ fontSize: 40, opacity: 0.3 }} />
                      <Typography variant="body2">Nenhum restaurante cadastrado.</Typography>
                    </Box>
                  )}
                </motion.div>
              )}

              {/* ── Tab 1: Notificacoes ── */}
              {tab === 1 && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  {/* Preset selector */}
                  <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}>
                    Perfil rapido
                  </Typography>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mt: 1, mb: 3 }}>
                    {presetsData &&
                      Object.entries(presetsData).map(([key, preset]) => (
                        <PresetCard
                          key={key}
                          presetKey={key}
                          preset={preset}
                          isSelected={config.preset === key}
                          onClick={() => handlePresetSelect(key)}
                        />
                      ))}
                  </Stack>

                  {/* Triggers */}
                  <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}>
                    Gatilhos
                  </Typography>

                  <Box sx={{ mt: 1 }}>
                    <Divider />
                    {TRIGGER_ORDER.map((triggerKey) => {
                      const trigger = config.triggers[triggerKey];
                      if (!trigger) return null;
                      return (
                        <TriggerRow
                          key={triggerKey}
                          triggerKey={triggerKey}
                          trigger={trigger}
                          label={triggerLabels[triggerKey] || triggerKey}
                          templateVariables={templateVariables}
                          testPhone={testPhone}
                          sendingTest={sendingTest}
                          onToggle={() => handleTriggerToggle(triggerKey)}
                          onTemplateChange={(value) => handleTemplateChange(triggerKey, value)}
                          onInsertVariable={(variable) => handleInsertVariable(triggerKey, variable)}
                          onSendTest={() => handleSendTest(triggerKey)}
                        />
                      );
                    })}
                  </Box>
                </motion.div>
              )}
            </Box>

            {/* Sticky footer */}
            <Box
              sx={{
                px: 3,
                py: 2,
                borderTop: `1px solid ${theme.palette.divider}`,
                backgroundColor: theme.palette.background.paper,
              }}
            >
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }}>
                <TextField
                  label="Telefone para teste"
                  size="small"
                  value={testPhone}
                  onChange={(e) => setTestPhone(e.target.value)}
                  placeholder="11999999999"
                  sx={{ flex: 1, maxWidth: 260 }}
                />
                <Box sx={{ flex: 1 }} />
                <Button
                  variant="contained"
                  onClick={handleSave}
                  disabled={saving}
                  startIcon={saving ? <CircularProgress size={15} /> : null}
                  sx={{
                    borderRadius: 2,
                    textTransform: "none",
                    fontWeight: 600,
                    backgroundColor: WA_GREEN,
                    "&:hover": { backgroundColor: WA_GREEN_DARK },
                    minWidth: 110,
                    boxShadow: "none",
                  }}
                >
                  {saving ? "Salvando..." : "Salvar"}
                </Button>
              </Stack>
            </Box>
          </Box>
        ) : null}
      </ModalContent>
    </AnimatedModal>
  );
}
